/* ── Agent Run Orchestration — shared domain model ──────────────────────────
 *
 * The "Agent Run" layer: a user-issued mission that a CTO agent plans, splits
 * into tasks, delegates to sub-agents, gates with approvals, and reports on
 * live. This file is client-safe (no server imports) — both API routes and UI
 * components import from here.
 *
 * NOTE: distinct from the legacy `agent_runs` observability table
 * (src/server/agent-runs.ts). That records one flat row per agent invocation;
 * an `AgentRun` here is a node of a hierarchical, event-sourced mission tree.
 */

/* ── Enums ── */

export type AgentRole =
  | "cto"
  | "architect"
  | "backend"
  | "frontend"
  | "ux"
  | "qa"
  | "security"
  | "support"
  | "release";

export const AGENT_ROLES: readonly AgentRole[] = [
  "cto", "architect", "backend", "frontend", "ux", "qa", "security", "support", "release",
];

export type RunStatus =
  | "queued"
  | "planning"
  | "delegating"
  | "running"
  | "awaiting_approval"
  | "blocked"
  | "completed"
  | "failed"
  | "cancelled";

/** Observability hint within a status — never gates anything. */
export type RunSubstate =
  | "planning"
  | "delegating"
  | "executing"
  | "waiting_child"
  | "waiting_approval"
  | "retrying"
  | null;

export type RunPriority = "critical" | "high" | "normal" | "low";

export type TaskStatus =
  | "queued"
  | "running"
  | "awaiting_approval"
  | "blocked"
  | "completed"
  | "failed"
  | "cancelled"
  | "skipped";

export type ApprovalType =
  | "plan"
  | "risky_patch"
  | "deployment"
  | "retry"
  | "security_override"
  /** A UX-architect UI change that removes a section, alters a primary action,
   *  hides technical detail, or restructures navigation — gated before apply. */
  | "ui_change_approval"
  /** Proceed despite a UX regression the architect could not clear itself. */
  | "ux_regression_override"
  /** Fase-5 execution-side gate: agent proposes to land a commit on the
   *  repo branch. Distinct from `risky_patch` (which reviews the patch
   *  content itself); `commit` gates the actual VCS write. The two
   *  exist side-by-side so audit + policy can distinguish them. */
  | "commit"
  /** Fase-5 execution-side gate: a PR is ready to merge. When tenant
   *  policy requires CI-green for merge, the gate's open-condition
   *  carries the linked pipeline_run id. */
  | "pr_merge"
  /** Developer governance (mig 225): the human verdict on a submitted
   *  `development_executions` row. Anchored on a companion run
   *  (`kind = 'development'`), decided through the same chokepoint, which
   *  additionally enforces four-eyes (reviewer ≠ initiator) and the project
   *  role permission `development.execution.approve` (D18). */
  | "development_review"
  /** Golf C3 (mig 238): a human verdict on a PROPOSED INFRASTRUCTURE ACTION —
   *  today `infra.restart_workload`. Anchored on a companion run
   *  (`kind = 'infra'`), decided through the same chokepoint, and bound to the
   *  proposal digest frozen at gate-open (`proposal_ref` / `proposal_hash`) so
   *  the verdict can only ever authorise the exact target the approver saw.
   *  Deliberately NOT `deployment`: it carries no `pipeline_run_id` (a restart
   *  promotes no build) and must not enter the Argo-promote side-effect arm. */
  | "infra_action";

export type ApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "expired"
  /** Reviewer asked for changes — the third plan-gate verdict. The plan is sent
   *  back to planning with the reviewer's feedback; a fresh plan version + gate
   *  open, so this status is terminal for the row it sits on. */
  | "changes_requested";

/** A reviewer's verdict on a blocking gate. */
export type ApprovalDecision = "approve" | "request_changes" | "reject";

/** Channel through which a gate decision was recorded.
 *
 * Required on every call to `decideOrchestrationApproval` so cross-channel
 * parity is provable from the audit chain alone — Slack, signed email-token,
 * the UI ApprovalCenter and the policy sweep all converge on the same server
 * function, and the channel tag is the only field that should differ between
 * otherwise identical decisions. The fase-7 cross-channel parity test asserts
 * exactly this property.
 *
 *  - `ui`           → session-authenticated UI ApprovalCenter / patch panel.
 *  - `email_token`  → `/governance/approve` signed-link consumption.
 *  - `slack`        → Slack interactive Block Kit button or `/fix` slash command.
 *  - `teams`        → Microsoft Teams Adaptive-Card action or bot command.
 *  - `system`       → policy sweep (timeout auto-reject, stall escalation).
 *  - `command_center` → the Command Center's `approval.decide` capability. A
 *                     session-authenticated decision like `ui`, and recorded
 *                     as its own channel rather than folded into it: the
 *                     evidence the human decided on was the gate summary a
 *                     manifest rendered onto a confirmation card, not the
 *                     review screen, and a compliance reader must be able to
 *                     tell those apart without reading the audit payload. The
 *                     capability is STRICTER than the UI route (it also
 *                     demands a 2FA-satisfied session), so this channel never
 *                     marks a weaker decision than `ui` does.
 */
export type DecisionChannel =
  | "ui"
  | "email_token"
  | "slack"
  | "teams"
  | "system"
  | "command_center";

/**
 * Gate types that accept the `request_changes` verdict — the canonical contract
 * shared by the API guard (`decideOrchestrationApproval`) and the UI
 * (`ApprovalCenter.PendingCard`). Before this lived in one place the UI exposed
 * the button for gates the server rejected, and vice versa (audit finding M2):
 * a `request_changes` POST returned 422 while the user already saw the button.
 *
 *  - `plan`               → re-plan with reviewer feedback as the open question.
 *  - `risky_patch`        → send the patch back to the dev agent; note = feedback.
 *  - `security_override`  → same as risky_patch.
 *  - `commit`             → reviewer wants the commit content revised before
 *                           landing on the branch; the dev agent re-prepares
 *                           the commit with the note as the brief.
 *  - `development_review` → the execution goes back to `changes_requested`;
 *                           the developer resumes it and re-submits, which
 *                           opens a fresh gate on the same companion run.
 *
 * Other gates are binary approve/reject — there is no revisable artefact:
 *   - `deployment`, `retry`               — operational decisions, not content
 *   - `ui_change_approval`, `ux_regression_override` — UX architect verdicts
 *   - `pr_merge`                          — merge or don't; revision is on
 *                                           the underlying commit/patch via
 *                                           `risky_patch` / `commit`
 */
export const REQUEST_CHANGES_SUPPORTED_TYPES: ReadonlySet<ApprovalType> = new Set<ApprovalType>([
  "plan", "risky_patch", "security_override", "commit", "development_review",
]);

export function supportsRequestChanges(type: ApprovalType): boolean {
  return REQUEST_CHANGES_SUPPORTED_TYPES.has(type);
}

export type ActorKind = "user" | "agent" | "system";

/** Event types written to the hash-chained `orchestration_events` log. */
export type OrchestrationEventType =
  | "run.created"
  | "run.planning"
  | "run.planned"
  | "run.delegating"
  | "run.running"
  | "run.blocked"
  | "run.awaiting_approval"
  | "run.resumed"
  | "run.completed"
  | "run.failed"
  | "run.cancelled"
  | "task.created"
  | "task.delegated"
  | "task.started"
  | "task.progress"
  | "task.output"
  | "task.completed"
  | "task.failed"
  | "task.skipped"
  | "approval.requested"
  | "approval.approved"
  | "approval.rejected"
  /** A reviewer asked for changes on a patch-backed gate — the patch is returned
   *  to the dev agent for revision (distinct from plan.changes_requested). */
  | "approval.changes_requested"
  /** Staged gate (mig 328): one stage decided; the gate stays open until
   *  the last stage (or a no) settles it. */
  | "approval.stage_decided"
  /** Staged gate: a stage passed its deadline and its timeout was claimed. */
  | "approval.stage_overdue"
  /** A previously-unbound execution-side gate gained its CI signal: a
   *  `pipeline_runs.id` was wired into the approval after the gate already
   *  existed. Late binding happens when the CI run arrives after the gate
   *  opens (typical for `pr_merge` — the GitHub App opens the gate on
   *  `pull_request.opened`, the workflow_run event lands minutes later). */
  | "approval.pipeline_run_bound"
  /** A patch-backed gate (risky_patch / security_override / commit) was found
   *  at decide time to be bound to a proposal that has since changed — the
   *  patch it froze was edited or removed (mig 228). The gate is closed
   *  UNDECIDED (status `expired`, exactly like the plan-version invalidation
   *  it mirrors); a fresh gate must be opened on the new proposal. */
  | "approval.invalidated"
  /** An open gate was closed WITHOUT a verdict because its subject is gone and
   *  no operator can ever answer it (the workspace carrying the patch under
   *  review was deleted, the mission was abandoned). `expirePendingApprovalsForRoot`
   *  used to do this as a silent UPDATE — the gate vanished from the pending
   *  tally with nothing on the timeline explaining why. */
  | "approval.expired"
  /* ── Plan lifecycle ──
   * The versioned, structured execution plan (orchestration_plans). Like the
   * ux.* events these are append-only string additions — orchestration_events
   * has no CHECK constraint, so no schema change is needed for them. */
  | "plan.created"
  | "plan.updated"
  | "plan.approval_requested"
  | "plan.approved"
  | "plan.rejected"
  | "plan.changes_requested"
  | "plan.approval_invalidated"
  /** A newer plan version landed on top of an APPROVED plan gate. Distinct
   *  from `plan.approval_invalidated`: the verdict is preserved (mig 270
   *  superseded_at / superseded_by_plan_version) and simply no longer
   *  authorises the new version. */
  | "plan.approval_superseded"
  | "agent.reasoning"
  | "artifact.produced"
  | "retry.scheduled"
  /* ── Workflow UX Architect ──
   * Emitted by the `ux` role as it assesses an operator workflow, captures
   * screenshots, proposes/implements a UI change and runs regression checks.
   * The DB `orchestration_events.type` column has no CHECK constraint, so
   * these are append-only additions — no schema change needed for events. */
  | "ux.assessment_started"
  | "ux.issue_detected"
  | "ux.screenshot_captured"
  | "ux.change_proposed"
  | "ux.patch_generated"
  | "ux.checks_started"
  | "ux.checks_passed"
  | "ux.regression_detected"
  | "ux.approval_requested"
  | "ux.task_completed"
  /** A patch was produced for the mission. Promoted to the `patch_generated`
   *  operational milestone (build-mission-timeline.ts). Emitted by the real
   *  patch path and by the demo replay leaf executor. Append-only string
   *  addition — `orchestration_events.type` has no CHECK constraint. */
  | "patch.generated"
  /* ── Sandbox verdicts (orchestration/sandbox-verdict-signal.ts) ──
   * What the verification of the mission's patch concluded. Written for every
   * terminal run, including one that finishes after the mission itself has —
   * which is the normal case for a slow validation, and used to leave the
   * timeline ending on "Execution completed" with the verdict nowhere.
   * `sandbox.blocked` is deliberately NOT `sandbox.failed`: the environment
   * failed, so nothing was proven about the patch. */
  | "sandbox.passed"
  | "sandbox.failed"
  | "sandbox.blocked";

/* ── Records ── */

export type ArtifactRef = {
  kind:
    | "patch"
    | "verification_report"
    | "agent_job"
    | "sandbox_run"
    | "issue"
    | "child_run"
    /* ── Workflow UX Architect artifacts ──
     * Real records produced by the `ux` role. The backing store lands in a
     * later batch; these kinds are declared up-front so the type contract is
     * stable across batches. */
    | "screenshot"
    | "visual_regression_report"
    | "ux_assessment"
    | "selector_check_report"
    /** Browser verification of a sandbox PREVIEW — the customer's application
     *  as it runs behind the preview gateway, not a FixControl route
     *  (migration 278). */
    | "preview_verification_report";
  id: string;
  label?: string;
  /** App-relative deep link, when one exists. */
  url?: string;
};

/** What a root run IS (mig 192). `mission` — the normal planned/delegated
 *  mission tree. `deployment` — a gate-only lifecycle root created when a
 *  deploy event arrives for an already-terminal mission: it is born in
 *  `awaiting_approval`, carries exactly one `deployment` gate, is never
 *  planned or delegated, and is settled by the gate's verdict.
 *  `development` — (mig 225) the companion root that anchors the
 *  `development_review` gate for one `development_executions` row. Same
 *  discipline as `deployment`: never planned or delegated, born parked in
 *  `awaiting_approval`, settled by the gate's verdict.
 *  `infra` — (mig 238) the companion root that anchors the `infra_action`
 *  gate for one proposed infrastructure action. Same discipline again: never
 *  planned or delegated, born parked in `awaiting_approval`, settled by the
 *  gate's verdict; its `result.infraAction` carries the frozen proposal the
 *  digest was taken over. */
export type RunKind = "mission" | "deployment" | "development" | "infra";

export type AgentRun = {
  id: string;
  tenant: string;
  issueKey: string | null;
  /** Project this run belongs to (mig 161) — derived from the issue at
   *  createRun. null = legacy/unassigned; consumers must tolerate that. */
  projectId: string | null;
  /** The issue's `contentRevision` at the moment this run was created
   *  (mig 299). null = unknown: the run predates the column, or it has no
   *  issue. A run whose stamp is BELOW the issue's current revision planned,
   *  patched and was approved against an earlier description — every surface
   *  that shows its output says so rather than letting it read as current. */
  issueContentRevision: number | null;
  parentRunId: string | null;
  rootRunId: string;
  agentRole: AgentRole;
  title: string;
  instruction: string;
  status: RunStatus;
  substate: RunSubstate;
  priority: RunPriority;
  rowVersion: number;
  createdBy: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  /** Soft-archive stamp (mig 151), root runs only: set when an operator
   *  removed the mission from the cross-mission list. The run and its full
   *  history stay in the DB — archived missions remain openable by id and
   *  are restorable from the missions list' archived view. */
  archivedAt: string | null;
  /** Demo scenario this run plays (src/server/demo/scenarios.ts), e.g.
   *  `GOLDEN_IDEMPOTENCY`. null = a normal (non-demo) mission. */
  demoScenarioId: string | null;
  /** Demo execution mode: `replay` (fixture-backed, deterministic) or `live`
   *  (real clone/test/patch). null = a normal (non-demo) mission. */
  demoMode: "replay" | "live" | null;
  /** mission | deployment (mig 192) | development (mig 225) — see
   *  {@link RunKind}. */
  kind: RunKind;
  /** deployment-kind only: the pipeline_runs row this lifecycle governs —
   *  its identity (one lifecycle per tenant + pipeline_run). null on
   *  mission-kind runs. */
  deploymentPipelineRunId: string | null;
  /** development-kind only (mig 225): the `development_executions` row this
   *  companion run governs — its identity (one companion run per tenant +
   *  execution). null on every other kind. */
  developmentExecutionId: string | null;
  /** infra-kind only (mig 238): the stable key of the infrastructure action
   *  this companion run governs (e.g. `infra-restart:<namespace>/<workload>`)
   *  — its identity (one companion run per tenant + key). null on every other
   *  kind. */
  infraRequestKey: string | null;
};

export type AgentRunEvent = {
  seq: number;
  runId: string;
  rootRunId: string;
  type: OrchestrationEventType;
  taskId: string | null;
  actorKind: ActorKind;
  actorId: string | null;
  payload: Record<string, unknown>;
  occurredAt: string;
  prevHash: string | null;
  thisHash: string;
};

export type AgentTask = {
  id: string;
  runId: string;
  rootRunId: string;
  tenant: string;
  assignedAgent: AgentRole;
  childRunId: string | null;
  title: string;
  description: string;
  rationale: string;
  status: TaskStatus;
  blocking: boolean;
  requiresApproval: boolean;
  sortOrder: number;
  outputSummary: string | null;
  artifactRefs: ArtifactRef[];
  rowVersion: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

/** The resulting action an approval decision drove — the "what happened"
 *  half of the audit ledger. Stable, queryable enum (not free prose) so a
 *  SIEM / compliance export can group decisions by outcome. */
export const DECISION_CONSEQUENCES = [
  "plan_approved",
  "mission_stopped",
  "patch_promoted",
  "patch_rejected",
  "revision_requested",
  "rollout_resumed",
  "deployment_approved",
  /** docs/PLAN-generic-cicd-gates.md policy decision 9 — the deployment
   *  REJECTION pair, mirroring the `infra_action` triple below. Argo Rollouts
   *  has no rejection primitive, so a rejected Argo gate records a verdict and
   *  leaves the rollout paused; every other CI/CD host in scope (GitHub
   *  Actions, GitLab, Jenkins) DOES have one, and FixControl uses it — the run
   *  then fails visibly at the customer, which IS the enforcement. Once a
   *  rejection has a side-effect, the generic `rejected` can no longer tell
   *  "we said no" apart from "we said no and the host enforced it".
   *    `deployment_rejection_requested` is the INTENT frozen with the verdict
   *      (a rejection write WILL be dispatched);
   *    `deployment_rejected` is the REALISED value once that job succeeded
   *      WITH an external reference (the host recorded the rejection);
   *    `rejected` (below) stays the realised fallback: no capability wired, or
   *      the write did not land — the verdict stands, the host was not told.
   *
   *  `decision_consequence` is a plain TEXT column (mig 182, no CHECK), so
   *  these are a TypeScript-only change — which also means the database cannot
   *  enforce the vocabulary and the full enum is pinned in test:decision-ledger
   *  instead. */
  "deployment_rejection_requested",
  "deployment_rejected",
  /** Golf C3 (mig 238) — the `infra_action` triple, same intent/realised shape
   *  as the deployment pair above:
   *    `workload_restart_requested` is the INTENT frozen with the verdict (an
   *      `infra.restart_workload` job will be dispatched);
   *    `workload_restarted` is the realised consequence once that job durably
   *      succeeded WITH an external reference (the receiver acted);
   *    `workload_restart_failed` is the realised consequence when the approve
   *      stands but the restart did not happen (capability unwired, receiver
   *      unreachable, digest refusal, dead-letter). */
  "workload_restart_requested",
  "workload_restarted",
  "workload_restart_failed",
  "approved",
  "rejected",
] as const;

/** The stable, queryable audit vocabulary a SIEM export groups on. Derived
 *  from {@link DECISION_CONSEQUENCES} so the runtime list and the type can
 *  never drift — the database has no CHECK to catch it. */
export type DecisionConsequence = (typeof DECISION_CONSEQUENCES)[number];

/** Which path settled the realised `decision_consequence` (mig 193):
 *  `decide` — inline at the end of the decision call; `outbox` — the
 *  argo.promote job's durable terminal state (completion hook); `recovery` —
 *  the boot sweep, re-derived from durable state after a crash. */
export type ConsequenceSource = "decide" | "outbox" | "recovery";

export type ApprovalRequest = {
  id: string;
  runId: string;
  rootRunId: string;
  tenant: string;
  taskId: string | null;
  type: ApprovalType;
  title: string;
  description: string;
  status: ApprovalStatus;
  /** For a `plan` gate, the exact plan version this approval authorizes. An
   *  approval binds to one version; a newer plan invalidates it. Null otherwise. */
  planVersion: number | null;
  /** Fase-5 CI-green guardrail. When set, the gate is bound to a
   *  `pipeline_runs` row (same tenant); `decideOrchestrationApproval`
   *  refuses an `approve` decision unless the linked pipeline run is in
   *  the `succeeded` phase. Null on plan / risky_patch / retry / etc. */
  pipelineRunId: string | null;
  /** Patch-backed gates (risky_patch / security_override / commit), mig 228 —
   *  the `workspace_patches` id this gate was opened over, frozen at gate-open
   *  so the verdict promotes the proposal the approver actually saw instead of
   *  whatever `findLatestPatchForIssue` returns at decide time. Null for gates
   *  that bind differently (plan → planVersion; deployment / pr_merge →
   *  pipelineRunId), for rows predating mig 228, and for a patch-backed gate
   *  opened before any patch existed. */
  proposalRef: string | null;
  /** `computePatchFingerprint` of {@link proposalRef} at gate-open — the same
   *  digest the sandbox gate binds its runs to. Re-verified at decide time; a
   *  mismatch invalidates the gate (`approval.invalidated`) rather than letting
   *  the verdict propagate to changed content. Null ⇒ unbound (legacy
   *  behaviour). */
  proposalHash: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  /** The channel the decision was recorded on (ui | email_token | slack |
   *  system), persisted next to decided_by. Null while pending, or for a
   *  legacy decision taken before the column was populated. Drives the
   *  "via Slack" / "via FixControl" source label in the decision ledger. */
  decidedChannel: DecisionChannel | null;
  /** Audit-grade decision ledger (mig 182), frozen at decide time next to
   *  decided_by / decided_at / channel so a compliance reader reconstructs
   *  the decision without joining to mutable governance / posture / pipeline
   *  tables. All null while pending or when a snapshot couldn't be resolved
   *  (best-effort — snapshotting never blocks a decision). */
  decidedRole: string | null;
  /** The resolved approver chain that governed this gate (route name, roles,
   *  mode, quorum, posture expansion), as in force at decide time. */
  approvalRouteSnapshot: import("./orchestration-approval-context").ApprovalChain | null;
  /** The evidence packet (blast tags, affected files, policy reason, event
   *  risk, pipeline-run deploy context) the approver saw at decide time. */
  evidenceSnapshot: import("./orchestration-approval-context").ApprovalContext | null;
  /** The governance policy snapshot in force, keyed by the tenant's last
   *  policy edit (governance_settings.updated_at). */
  policyVersion: string | null;
  /** The resulting action the decision drove (plan_approved / mission_stopped
   *  / patch_promoted / revision_requested / rollout_resumed / … ), settled
   *  from the verdict's realised side-effects — inline, from the durable
   *  argo.promote outbox completion, or by the crash-recovery sweep. */
  decisionConsequence: DecisionConsequence | null;
  /** What the verdict was MEANT to drive (mig 193), frozen atomically with
   *  the verdict BEFORE any side-effect is dispatched. The crash-recovery
   *  anchor: lets the boot sweep settle (or re-drive) the realised
   *  consequence from durable state when the process died mid side-effects. */
  intendedConsequence: DecisionConsequence | null;
  /** When + through which path the realised consequence was settled. */
  consequenceSettledAt: string | null;
  consequenceSource: ConsequenceSource | null;
  createdAt: string;
  /** Transient (not persisted): the tenant's ORCH_GATE_APPROVE_SEMANTICS,
   *  attached at the API boundary so the gate UI can render what "approve"
   *  does for a CTO `risky_patch` gate. Absent → treat as "revise". */
  approveSemantics?: "revise" | "accept";
  /** Transient (not persisted): structured decision context — blast radius,
   *  affected-files sample, policy reason — attached at the API boundary so
   *  every gate (not just `plan`) carries the evidence its approver needs.
   *  See `src/lib/orchestration-approval-context.ts`. */
  context?: import("./orchestration-approval-context").ApprovalContext;
  /** Transient (not persisted): operator-facing prose composed from the
   *  context + summary + (optional) posture. Drives the decision-first UI
   *  surface, the Slack Block-Kit plan-card and the email-token template
   *  off a single source. See `src/lib/orchestration-decision-brief.ts`. */
  decisionBrief?: import("./orchestration-decision-brief").DecisionBrief;
  /** Transient (not persisted): a STAGED gate's progress (mig 328), attached
   *  at the API boundary. Absent → the gate is not staged. */
  stages?: import("./governance/staged-approvals").StageSummary;
};

/** The bundle the run-detail API returns and the Agent Run Panel renders. */
export type RunBundle = {
  /** The mission root (root_run_id === id). */
  root: AgentRun;
  /** Root + every descendant — the delegation tree. */
  runs: AgentRun[];
  tasks: AgentTask[];
  approvals: ApprovalRequest[];
  /** Recent slice of the timeline, `seq` ascending. */
  events: AgentRunEvent[];
};

/* ── Status helpers ── */

const TERMINAL_RUN: ReadonlySet<RunStatus> = new Set(["completed", "failed", "cancelled"]);
const TERMINAL_TASK: ReadonlySet<TaskStatus> = new Set([
  "completed", "failed", "cancelled", "skipped",
]);

export function isRunFinished(status: RunStatus): boolean {
  return TERMINAL_RUN.has(status);
}
export function isTaskFinished(status: TaskStatus): boolean {
  return TERMINAL_TASK.has(status);
}

/** Tasks done / total — drives the run-header progress bar. */
export function runProgress(tasks: AgentTask[]): { done: number; total: number } {
  return {
    done: tasks.filter((t) => isTaskFinished(t.status)).length,
    total: tasks.length,
  };
}

/* ── Display metadata (client-safe) ── */

export const ROLE_META: Record<
  AgentRole,
  { label: string; short: string; description: string; icon: string; cls: string }
> = {
  cto: {
    label: "CTO", short: "CTO",
    description: "Plans the mission, splits the work, delegates to sub-agents and owns the technical gate.",
    icon: "cpu", cls: "text-brand-300",
  },
  architect: {
    label: "Architect", short: "Architect",
    description: "Locates the relevant code, traces the defect and produces the implementation brief.",
    icon: "compass", cls: "text-status-info",
  },
  backend: {
    label: "Backend Engineer", short: "Backend",
    description: "Implements the server-side fix and produces a patch with tests.",
    icon: "server", cls: "text-status-info",
  },
  frontend: {
    label: "Frontend Engineer", short: "Frontend",
    description: "Implements the UI-side fix and produces a patch.",
    icon: "monitor", cls: "text-status-info",
  },
  ux: {
    label: "Workflow UX Architect", short: "UX Architect",
    description:
      "Assesses operator workflows — reduces cognitive load, sharpens primary actions and information hierarchy, and guards UI changes with screenshot-based regression checks.",
    icon: "layout-dashboard", cls: "text-brand-300",
  },
  qa: {
    label: "QA Engineer", short: "QA",
    description: "Verifies the patch — checklist, regression risks and an acceptance verdict.",
    icon: "shield-check", cls: "text-status-low",
  },
  security: {
    label: "Security Reviewer", short: "Security",
    description: "Reviews risk flags and regression surface before anything ships.",
    icon: "shield", cls: "text-status-high",
  },
  support: {
    label: "Support Analyst", short: "Support",
    description: "Drafts the customer-facing update once the mission resolves.",
    icon: "headphones", cls: "text-brand-300",
  },
  release: {
    label: "Release Manager", short: "Release",
    description: "Owns the gated apply / deployment of an approved patch.",
    icon: "rocket", cls: "text-status-medium",
  },
};

export const RUN_STATUS_META: Record<
  RunStatus,
  { label: string; cls: string; dot: string }
> = {
  queued:            { label: "Queued",            cls: "text-fg-muted",        dot: "bg-fg-muted" },
  planning:          { label: "Planning",          cls: "text-status-info",     dot: "bg-status-info" },
  delegating:        { label: "Delegating",        cls: "text-status-info",     dot: "bg-status-info" },
  running:           { label: "Running",           cls: "text-status-info",     dot: "bg-status-info" },
  awaiting_approval: { label: "Awaiting approval", cls: "text-status-medium",   dot: "bg-status-medium" },
  blocked:           { label: "Blocked",           cls: "text-status-high",     dot: "bg-status-high" },
  completed:         { label: "Completed",         cls: "text-status-low",      dot: "bg-status-low" },
  failed:            { label: "Failed",            cls: "text-status-critical", dot: "bg-status-critical" },
  cancelled:         { label: "Cancelled",         cls: "text-fg-dim",          dot: "bg-fg-dim" },
};

export const TASK_STATUS_META: Record<
  TaskStatus,
  { label: string; cls: string; dot: string }
> = {
  queued:            { label: "Queued",            cls: "text-fg-muted",        dot: "bg-fg-muted" },
  running:           { label: "Running",           cls: "text-status-info",     dot: "bg-status-info" },
  awaiting_approval: { label: "Awaiting approval", cls: "text-status-medium",   dot: "bg-status-medium" },
  blocked:           { label: "Blocked",           cls: "text-status-high",     dot: "bg-status-high" },
  completed:         { label: "Completed",         cls: "text-status-low",      dot: "bg-status-low" },
  failed:            { label: "Failed",            cls: "text-status-critical", dot: "bg-status-critical" },
  cancelled:         { label: "Cancelled",         cls: "text-fg-dim",          dot: "bg-fg-dim" },
  skipped:           { label: "Skipped",           cls: "text-fg-dim",          dot: "bg-fg-dim" },
};

export const APPROVAL_TYPE_META: Record<
  ApprovalType,
  { label: string; description: string; icon: string; cls: string }
> = {
  plan: {
    label: "Plan approval",
    description: "Approve the CTO's mission plan before any work starts.",
    icon: "list-checks", cls: "text-status-info",
  },
  risky_patch: {
    label: "Risky patch",
    description: "A patch the CTO gate flagged as risky needs a human sign-off.",
    icon: "git-pull-request", cls: "text-status-high",
  },
  deployment: {
    label: "Deployment",
    description: "Approve applying / deploying an approved patch.",
    icon: "rocket", cls: "text-status-medium",
  },
  retry: {
    label: "Retry after failure",
    description: "A task failed — approve a retry before the mission continues.",
    icon: "rotate-ccw", cls: "text-status-high",
  },
  security_override: {
    label: "Security override",
    description: "Proceed despite an unresolved security concern.",
    icon: "shield-alert", cls: "text-status-critical",
  },
  ui_change_approval: {
    label: "UI change",
    description:
      "Approve a UI change that removes a section, alters a primary action, hides technical detail, or restructures navigation.",
    icon: "pencil-ruler", cls: "text-status-high",
  },
  ux_regression_override: {
    label: "UX regression override",
    description: "Proceed despite a UX regression the architect could not clear automatically.",
    icon: "scan-eye", cls: "text-status-critical",
  },
  commit: {
    label: "Commit",
    description:
      "Approve landing the proposed commit on the repo branch. The reviewer can request changes — the dev agent re-prepares the commit with the note as the brief.",
    icon: "git-commit", cls: "text-status-high",
  },
  pr_merge: {
    label: "PR merge",
    description:
      "Approve merging the open PR. When tenant policy requires CI-green for merge, the gate carries the linked pipeline_run.",
    icon: "git-merge", cls: "text-status-high",
  },
  development_review: {
    label: "Development review",
    description:
      "Approve submitted developer work. The reviewer must be someone other than the initiator and needs the project permission development.execution.approve; requesting changes sends the execution back to the developer.",
    icon: "code", cls: "text-status-high",
  },
  infra_action: {
    label: "Infrastructure action",
    description:
      "Approve a proposed infrastructure action (restart a workload). The gate is bound to the exact target the approver was shown — connection, namespace, workload and parameters — and a changed proposal invalidates it instead of authorising a different one.",
    icon: "server-cog", cls: "text-status-critical",
  },
};

/* ── Timeline rendering ── */

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" && v.length > 0 ? v : fallback;
}

/**
 * One-line human description of a timeline event. The orchestration engine
 * controls the payload shape, so this stays a simple, total mapping — never
 * throws, always returns something printable.
 */
export function describeEvent(ev: AgentRunEvent, locale: import("@/i18n/config").SupportedLocale = "en"): string {
  const p = ev.payload ?? {};
  const pr = (k: string, d = "") => str((p as Record<string, unknown>)[k], d);
  const nl = locale === "nl";
  const L = (en: string, du: string) => (nl ? du : en);
  const role = pr("role");
  const roleLabel = role && role in ROLE_META ? ROLE_META[role as AgentRole].label : L("Executor", "Uitvoerder");
  const title = pr("title");
  // Prefer the resolved approver name over the raw actor id; append the
  // decision channel so the timeline reads the who/what/WHERE audit:
  // "approved by Alex via Slack".
  const by = pr("byName") || pr("by");
  const ch = pr("channel");
  const chLabel = ch
    ? (nl
        ? ({ ui: "FixControl", slack: "Slack", teams: "Teams", email_token: "e-mail", system: "systeem", command_center: "Command Center" } as Record<string, string>)[ch] ?? ch
        : ({ ui: "FixControl", slack: "Slack", teams: "Teams", email_token: "email", system: "system", command_center: "Command Center" } as Record<string, string>)[ch] ?? ch)
    : "";
  const via = chLabel ? ` via ${chLabel}` : "";
  const byS = by ? L(` by ${by}${via}`, ` door ${by}${via}`) : "";
  const v = (d = "?") => pr("version", d);
  switch (ev.type) {
    case "run.created":      return L(`Execution authorized${title ? `: ${title}` : ""}`, `Uitvoering geautoriseerd${title ? `: ${title}` : ""}`);
    case "run.planning":     return L(`${roleLabel} planning execution`, `${roleLabel} plant de uitvoering`);
    case "run.planned":      return L(`Execution plan ready — ${pr("taskCount", "0")} task(s)`, `Uitvoeringsplan klaar — ${pr("taskCount", "0")} taak/taken`);
    case "run.delegating":   return L(`Dispatching reviewed tasks`, `Beoordeelde taken worden verdeeld`);
    case "run.running":      return L(`Execution in progress`, `Uitvoering bezig`);
    case "run.blocked":      return L(`Execution blocked${title ? `: ${title}` : ""}`, `Uitvoering geblokkeerd${title ? `: ${title}` : ""}`);
    case "run.awaiting_approval": return L(`Execution paused — awaiting approval`, `Uitvoering gepauzeerd — wacht op goedkeuring`);
    case "run.resumed":      return L(`Execution resumed`, `Uitvoering hervat`);
    case "run.completed":    return L(`Execution completed`, `Uitvoering voltooid`);
    case "run.failed":       return L(`Execution failed${title ? `: ${title}` : ""}`, `Uitvoering mislukt${title ? `: ${title}` : ""}`);
    case "run.cancelled":    return L(`Execution cancelled`, `Uitvoering geannuleerd`);
    case "task.created":     return L(`Task created — ${title || "untitled"}`, `Taak aangemaakt — ${title || "naamloos"}`);
    case "task.delegated":   return L(`Dispatched to ${roleLabel} — ${title || "task"}`, `Toegewezen aan ${roleLabel} — ${title || "taak"}`);
    case "task.started":     return L(`${roleLabel} executing — ${title || "task"}`, `${roleLabel} voert uit — ${title || "taak"}`);
    case "task.progress":    return pr("message", L(`${roleLabel} in progress…`, `${roleLabel} bezig…`));
    case "task.output":      return `${roleLabel}: ${pr("summary", L("produced output", "produceerde output"))}`;
    case "task.completed":   return L(`${roleLabel} finished — ${title || "task"}`, `${roleLabel} klaar — ${title || "taak"}`);
    case "task.failed":      return L(`${roleLabel} failed — ${pr("reason", title || "task")}`, `${roleLabel} mislukt — ${pr("reason", title || "taak")}`);
    case "task.skipped":     return L(`Task skipped — ${title || "task"}`, `Taak overgeslagen — ${title || "taak"}`);
    case "approval.requested": return L(`Approval requested — ${title || "gate"}`, `Goedkeuring gevraagd — ${title || "poort"}`);
    case "approval.approved":  return L(`Approved — ${title || "gate"}${byS}`, `Goedgekeurd — ${title || "poort"}${byS}`);
    case "approval.rejected":  return L(`Rejected — ${title || "gate"}${byS}`, `Afgewezen — ${title || "poort"}${byS}`);
    case "approval.changes_requested": return L(`Changes requested — ${title || "gate"}${byS}`, `Wijzigingen gevraagd — ${title || "poort"}${byS}`);
    case "approval.stage_decided": return L(
      `Stage ${pr("step", "?")} of ${pr("totalSteps", "?")} (${pr("stageLabel", "stage")}) ${pr("decision", "decided")} — ${title || "gate"}${byS}`,
      `Fase ${pr("step", "?")} van ${pr("totalSteps", "?")} (${pr("stageLabel", "fase")}) ${pr("decision", "beslist")} — ${title || "poort"}${byS}`);
    case "approval.stage_overdue": return L(
      `Stage ${pr("step", "?")} (${pr("stageLabel", "stage")}) is overdue — ${pr("action", "")}`,
      `Fase ${pr("step", "?")} (${pr("stageLabel", "fase")}) is te laat — ${pr("action", "")}`);
    case "approval.pipeline_run_bound": return L(`CI run bound to gate — ${title || "gate"} (pipeline ${pr("pipelineRunId", "?")})`, `CI-run gekoppeld aan poort — ${title || "poort"} (pipeline ${pr("pipelineRunId", "?")})`);
    case "approval.invalidated": return L(`Approval invalidated — ${title || "gate"}: the patch changed after the gate opened`, `Goedkeuring ongeldig — ${title || "poort"}: de patch is gewijzigd nadat de poort openging`);
    case "approval.expired": return L(`Approval expired — ${title || "gate"}: ${pr("reason", "the gate can no longer be answered")}`, `Goedkeuring verlopen — ${title || "poort"}: ${pr("reason", "de poort kan niet meer worden beantwoord")}`);
    case "plan.created":       return L(`Execution plan v${v("1")} created`, `Uitvoeringsplan v${v("1")} aangemaakt`);
    case "plan.updated":       return L(`Execution plan revised to v${v()}`, `Uitvoeringsplan herzien naar v${v()}`);
    case "plan.approval_requested": return L(`Plan v${v()} submitted for approval`, `Plan v${v()} ingediend voor goedkeuring`);
    case "plan.approved":      return L(`Execution plan v${v()} approved${byS}`, `Uitvoeringsplan v${v()} goedgekeurd${byS}`);
    case "plan.rejected":      return L(`Execution plan v${v()} rejected${byS}`, `Uitvoeringsplan v${v()} afgewezen${byS}`);
    case "plan.changes_requested": return L(`Changes requested on plan v${v()}${byS}`, `Wijzigingen gevraagd op plan v${v()}${byS}`);
    case "plan.approval_invalidated": return L(`Plan v${v()} approval invalidated — the plan changed`, `Goedkeuring plan v${v()} ongeldig — het plan is gewijzigd`);
    case "plan.approval_superseded": return L(`Plan v${v()} approval superseded by v${pr("newVersion", "?")} — the recorded verdict is kept`, `Goedkeuring plan v${v()} vervangen door v${pr("newVersion", "?")} — het vastgelegde besluit blijft bewaard`);
    case "agent.reasoning":    return `${roleLabel}: ${pr("text", L("reasoning", "redenering"))}`;
    case "artifact.produced":  return L(`Artifact: ${pr("label", pr("kind", "produced"))}`, `Artefact: ${pr("label", pr("kind", "geproduceerd"))}`);
    case "retry.scheduled":    return L(`Retry scheduled (attempt ${pr("attempt", "?")})`, `Nieuwe poging gepland (poging ${pr("attempt", "?")})`);
    case "ux.assessment_started":  return L(`${roleLabel} started a UX assessment${title ? `: ${title}` : ""}`, `${roleLabel} startte een UX-beoordeling${title ? `: ${title}` : ""}`);
    case "ux.issue_detected":      return L(`UX issue — ${pr("concern", pr("text", "detected"))}`, `UX-probleem — ${pr("concern", pr("text", "gedetecteerd"))}`);
    case "ux.screenshot_captured": return L(`Screenshot captured — ${pr("label", pr("route", "route"))}`, `Screenshot vastgelegd — ${pr("label", pr("route", "route"))}`);
    case "ux.change_proposed":     return L(`${roleLabel} proposed a UI change — ${pr("text", title || "change")}`, `${roleLabel} stelde een UI-wijziging voor — ${pr("text", title || "wijziging")}`);
    case "ux.patch_generated":     return L(`UI patch generated — ${title || pr("patchId", "patch")}`, `UI-patch gegenereerd — ${title || pr("patchId", "patch")}`);
    case "ux.checks_started":      return L(`UI checks started${pr("route") ? ` on ${pr("route")}` : ""}`, `UI-controles gestart${pr("route") ? ` op ${pr("route")}` : ""}`);
    case "ux.checks_passed":       return L(`UI checks passed — no regression detected`, `UI-controles geslaagd — geen regressie gedetecteerd`);
    case "ux.regression_detected": return L(`UX regression — ${pr("reason", "checks failed")}`, `UX-regressie — ${pr("reason", "controles mislukt")}`);
    case "ux.approval_requested":  return L(`UX approval requested — ${title || pr("text", "gate")}`, `UX-goedkeuring gevraagd — ${title || pr("text", "poort")}`);
    case "sandbox.passed":     return L(`Verification passed — the change was built and tested`, `Verificatie geslaagd — de wijziging is gebouwd en getest`);
    case "sandbox.failed":     return L(`Verification failed — ${pr("failureKind", "the change did not pass")}`, `Verificatie mislukt — ${pr("failureKind", "de wijziging kwam er niet door")}`);
    case "sandbox.blocked":    return L(`Verification could not judge the change — ${pr("failureKind", "the environment failed")}`, `Verificatie kon de wijziging niet beoordelen — ${pr("failureKind", "de omgeving faalde")}`);
    default:                   return ev.type;
  }
}
